const API_URL = import.meta.env.VITE_API_URL || "http://localhost:5000/api/v1";

const getToken = () => localStorage.getItem("accessToken");
const getRefreshToken = () => localStorage.getItem("refreshToken");

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any = null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

const refreshAccessToken = async (): Promise<string> => {
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    throw new Error("No refresh token available");
  }

  const response = await fetch(`${API_URL}/auth/refresh`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ refreshToken }),
  });

  if (!response.ok) {
    throw new Error("Token refresh failed");
  }

  const data = await response.json();

  if (data.success && data.data?.token) {
    localStorage.setItem("accessToken", data.data.token);
    console.log("🔄 Access token refreshed successfully");

    window.dispatchEvent(new Event("token-refreshed"));

    return data.data.token;
  }

  throw new Error("Invalid refresh response");
};

export const apiCall = async (
  endpoint: string,
  options: RequestInit = {},
  _retry: boolean = true,
): Promise<any> => {
  const token = getToken();

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers,
  });

  const isAuthEndpoint =
    endpoint.includes("/auth/login") ||
    endpoint.includes("/auth/register") ||
    endpoint.includes("/auth/refresh");

  if (response.status === 401 && _retry && !isAuthEndpoint) {
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((_token) => {
          return apiCall(endpoint, options, false);
        })
        .catch((err) => {
          throw err;
        });
    }

    isRefreshing = true;

    try {
      const newToken = await refreshAccessToken();
      processQueue(null, newToken);

      return apiCall(endpoint, options, false);
    } catch (refreshError) {
      processQueue(refreshError, null);

      console.error("Token refresh failed, logging out...");
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("campus-itsm-user");

      window.dispatchEvent(new Event("token-expired"));

      throw new Error("Session expired. Please login again.");
    } finally {
      isRefreshing = false;
    }
  }

  if (!response.ok) {
    const contentType = response.headers.get("content-type") || "";
    let error: any = { message: "Request failed" };

    if (contentType.includes("application/json")) {
      error = await response
        .json()
        .catch(() => ({ message: "Request failed" }));
    } else {
      const text = await response.text().catch(() => "");
      error = { message: text || "Request failed" };
    }

    const isNotificationAuthNoise =
      response.status === 401 && endpoint.startsWith("/notifications");

    if (!isNotificationAuthNoise) {
      console.error("API Error:", error);
    }

    throw new Error(error.message || `HTTP ${response.status}`);
  }

  if (response.status === 204 || response.status === 205) {
    return { success: true };
  }

  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return response.json();
  }

  const text = await response.text().catch(() => "");
  if (!text) {
    return { success: true };
  }

  try {
    return JSON.parse(text);
  } catch {
    return { success: true, data: text };
  }
};

export const authAPI = {
  login: (email: string, password: string) =>
    apiCall("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),

  register: (data: any) =>
    apiCall("/auth/register", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  refresh: (refreshToken: string) =>
    apiCall("/auth/refresh", {
      method: "POST",
      body: JSON.stringify({ refreshToken }),
    }),

  logout: () => apiCall("/auth/logout", { method: "POST" }),
};

export const ticketAPI = {
  list: (filters?: any) => {
    const query = filters ? new URLSearchParams(filters).toString() : "";
    return apiCall(`/tickets${query ? `?${query}` : ""}`);
  },

  get: (id: string) => apiCall(`/tickets/${id}`),

  create: (data: any) =>
    apiCall("/tickets", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  update: (id: string, data: any) =>
    apiCall(`/tickets/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  delete: (id: string) => apiCall(`/tickets/${id}`, { method: "DELETE" }),

  updateStatus: (id: string, status: string) =>
    apiCall(`/tickets/${id}/status`, {
      method: "PUT",
      body: JSON.stringify({ status }),
    }),

  updatePriority: (id: string, priority: string) =>
    apiCall(`/tickets/${id}/priority`, {
      method: "PUT",
      body: JSON.stringify({ priority }),
    }),

  assign: (id: string, assignedId: string) =>
    apiCall(`/tickets/${id}/assign`, {
      method: "PUT",
      body: JSON.stringify({ assignedId }),
    }),

  escalate: (id: string, reason: string) =>
    apiCall(`/tickets/${id}/escalate`, {
      method: "PUT",
      body: JSON.stringify({ reason }),
    }),

  history: (id: string) => apiCall(`/tickets/${id}/history`),
};

export const commentAPI = {
  list: (ticketId: string) => apiCall(`/tickets/${ticketId}/comments`),

  add: (ticketId: string, data: { text: string; isInternal?: boolean }) =>
    apiCall(`/tickets/${ticketId}/comments`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
};

export const attachmentAPI = {
  list: (ticketId: string) => apiCall(`/tickets/${ticketId}/attachments`),

  upload: (ticketId: string, file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    const token = getToken();
    return fetch(`${API_URL}/tickets/${ticketId}/attachments`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    }).then((res) => res.json());
  },

  download: (ticketId: string, attachmentId: string) => {
    const token = getToken();
    const url = `${API_URL}/tickets/${ticketId}/attachments/${attachmentId}`;

    return fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }).then(async (res) => {
      if (!res.ok) {
        const error = await res
          .json()
          .catch(() => ({ message: "Download failed" }));
        throw new Error(error.message || "Download failed");
      }

      const contentDisposition = res.headers.get("Content-Disposition");
      const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
      const filename = filenameMatch ? filenameMatch[1] : "download";

      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);

      return { success: true, filename };
    });
  },
};

export const feedbackAPI = {
  get: (ticketId: string) => apiCall(`/tickets/${ticketId}/feedback`),

  submit: (
    ticketId: string,
    rating: number,
    comment?: string,
    isAnonymous?: boolean,
  ) =>
    apiCall(`/tickets/${ticketId}/feedback`, {
      method: "POST",
      body: JSON.stringify({ rating, comment, isAnonymous }),
    }),
};

export const categoryAPI = {
  list: (departmentId?: string) => {
    const query = departmentId ? `?departmentId=${departmentId}` : "";
    return apiCall(`/categories${query}`);
  },

  create: (data: any) =>
    apiCall("/categories", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  update: (id: string, data: any) =>
    apiCall(`/categories/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),
};

export const departmentAPI = {
  list: () => apiCall("/departments"),

  create: (data: any) =>
    apiCall("/departments", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  update: (id: string, data: any) =>
    apiCall(`/departments/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),
};

export const notificationAPI = {
  list: (page: number = 1, limit: number = 20, isRead?: boolean) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (isRead !== undefined) params.append("isRead", isRead.toString());
    return apiCall(`/notifications?${params.toString()}`);
  },

  unreadCount: () => apiCall("/notifications/unread-count"),

  markAsRead: (id: string) =>
    apiCall(`/notifications/${id}/read`, { method: "PUT" }),

  markAllAsRead: () => apiCall("/notifications/read-all", { method: "PUT" }),

  delete: (id: string) => apiCall(`/notifications/${id}`, { method: "DELETE" }),

  getEmailPreferences: () => apiCall("/notifications/email-preferences"),

  updateEmailPreferences: (preferences: Record<string, boolean>) =>
    apiCall("/notifications/email-preferences", {
      method: "PUT",
      body: JSON.stringify(preferences),
    }),
};

export const adminAPI = {
  getUsers: (filters?: any) => {
    const query = filters ? new URLSearchParams(filters).toString() : "";
    return apiCall(`/admin/users${query ? `?${query}` : ""}`);
  },

  createUser: (data: any) =>
    apiCall("/admin/users", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  updateUser: (id: string, data: any) =>
    apiCall(`/admin/users/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  deleteUser: (id: string) =>
    apiCall(`/admin/users/${id}`, { method: "DELETE" }),

  technicianStats: () => apiCall("/admin/technicians"),

  reports: (filters?: any) => {
    const query = filters ? new URLSearchParams(filters).toString() : "";
    return apiCall(`/admin/reports${query ? "?" + query : ""}`);
  },

  analytics: (params?: any) => {
    const query = params ? new URLSearchParams(params).toString() : "";
    return apiCall(`/admin/analytics${query ? "?" + query : ""}`);
  },

  slaStatistics: (params?: any) => {
    const query = params ? new URLSearchParams(params).toString() : "";
    return apiCall(`/admin/sla-statistics${query ? "?" + query : ""}`);
  },
};

export const technicianAPI = {
  tickets: () => apiCall("/technician/tickets"),
};
